import { createAction } from 'redux-api-middleware'
import { action as azureBlockUploadAction } from 'abu-redux-middleware'

import { HUBBLE_API_TOKEN } from '../../../store/middleware/APIUrl'
import { DateUtils, FileUtils, UrlUtils } from '../../../utils'
import { Media } from '../../../constants'
import Types from './types'

/**
 * Fetch media data
 * @param {Number} page Page to load
 * @param {Object} filters
 * @param {String} [filters.name]
 * @param {Boolean} [filters.pendingApproval]
 * @param {Boolean} [filters.pendingPush]
 * @param {Boolean} [filters.pendingTest]
 */
function fetchMedia(page = 0, { name = null, pendingApproval = false, pendingPush = false, pendingTest = false, tag = null } = {}) {
    const params = {
        order: 'created_at',
        desc: '1',
    }

    if (page > 0) {
        params.page = page
    }

    if (name) {
        params.name = name
    }

    if (pendingApproval) {
        params.pending_approval = 1
    }

    if (pendingTest) {
        params.pending_test = 1
    }

    if (pendingPush) {
        params.push_status = Media.pushStatus.pending
    }

    if (tag) {
        params.tags = tag
    }

    const query = UrlUtils.buildQueryString(params)
    const noFirstPage = page > 1

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media?${query}`,
        method: 'GET',
        types: [
            {
                type: Types.FETCH_MEDIA_REQUEST,
                meta: { noFirstPage },
            },
            {
                type: Types.FETCH_MEDIA_SUCCESS,
                meta: { noFirstPage },
            },
            Types.API_CALL_FAILURE,
        ],
        options: {
            abortable: true,
        },
    })
}

/**
 * Fetch single media data
 * @param {Number} id Media ID
 */
function fetchSingleMedia(id) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/${id}`,
        method: 'GET',
        types: [Types.FETCH_SINGLE_MEDIA_REQUEST, Types.FETCH_SINGLE_MEDIA_SUCCESS, Types.API_CALL_FAILURE],
    })
}

/**
 * Fetch single media data (same as above, but reducer does not crap)
 * @param {Number} id Media ID
 */
function fetchMediaDetails(id) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/${id}`,
        method: 'GET',
        types: [Types.FETCH_MEDIA_DETAILS_REQUEST, Types.FETCH_MEDIA_DETAILS_SUCCESS, Types.FETCH_MEDIA_DETAILS_FAILURE],
    })
}

/**
 * Create new media item
 * @param {Object} media
 * @param {Number} media.date_end
 * @param {Number} media.date_start
 * @param {Number} media.length
 * @param {String} media.name
 * @param {String} media.md5
 * @param {Number} media.size
 */
function createMedia(media = {}) {
    const { date_end, date_start, height, length, md5, name, path, type: fileType, size, width } = media
    const body = JSON.stringify({
        date_end: DateUtils.formatDB(date_end),
        date_start: DateUtils.formatDB(date_start),
        file_size: size,
        height,
        length: Math.round(length),
        name,
        md5,
        path,
        type: fileType,
        width,
    })

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media`,
        method: 'POST',
        types: [Types.CREATE_MEDIA_REQUEST, Types.CREATE_MEDIA_SUCCESS, Types.API_CALL_FAILURE],
        body,
    })
}

/**
 * Download a media item
 * @param mediaID The media id
 */
function downloadMedia(mediaID, lowQuality = false) {
    const low = lowQuality ? '/lowquality' : ''

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/${mediaID}/download${low}`,
        method: 'GET',
        types: [Types.DOWNLOAD_MEDIA_REQUEST, Types.DOWNLOAD_MEDIA_SUCCESS, Types.API_CALL_FAILURE],
    })
}

/**
 * Download a media thumbnail
 * @param mediaID The media id
 */
function downloadThumbnail(mediaID) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/${mediaID}/thumbnail`,
        method: 'GET',
        types: [Types.DOWNLOAD_MEDIA_THUMBNAIL_REQUEST, Types.DOWNLOAD_MEDIA_THUMBNAIL_SUCCESS, Types.DOWNLOAD_MEDIA_THUMBNAIL_FAIL],
    })
}

function uploadCompleted(mediaID) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/${mediaID}/upload_complete`,
        method: 'POST',
        types: [Types.UPLOAD_COMPLETED_MEDIA_REQUEST, Types.UPLOAD_COMPLETED_MEDIA_SUCCESS, Types.API_CALL_FAILURE],
    })
}

/**
 * Upload media
 * @param {File} file
 * @param {Object} media
 * @param {Number} media.date_end
 * @param {Number} media.date_start
 * @param {Number} media.length
 * @param {String} media.name
 * @param {String} media.md5
 * @param {Number} media.size
 * @param {Object} [callbacks]
 * @param {Function} [callbacks.onSuccess]
 * @param {Function} [callbacks.onError]
 * @param {Function} [callbacks.onProgress]
 */
function uploadMedia(file, media, callbacks = {}) {
    // Get name from file
    const { name: fileName, type: fileType } = file

    // Get new name for the file from media
    const { name } = media

    // Gets extension
    const extension = FileUtils.getFileExtension(fileName)

    // Compose new file name
    const uploadName = name ? `${name}.${extension}` : fileName

    return async (dispatch) => {
        try {
            // Gets md5 hash of the file
            let md5
            if (media.size > 1073741824) md5 = await FileUtils.getMD5(file)
            else md5 = FileUtils.checksum(await FileUtils.readFile(file))

            // Creates the entry in the db
            const { type, payload: mediaRecord } = await dispatch(
                createMedia({
                    ...media,
                    path: uploadName,
                    md5,
                    type: fileType,
                })
            )

            if (type !== Types.CREATE_MEDIA_SUCCESS) {
                throw new Error('Error when creating media')
            }

            const { id, upload_url: urlToUpload } = mediaRecord || {}

            if (!id) {
                throw new Error('Cannot find media ID')
            }

            await dispatch(azureBlockUploadAction(urlToUpload, file, callbacks))

            await dispatch(uploadCompleted(id))

            return mediaRecord
        } catch (err) {
            const { onError } = callbacks
            if (onError instanceof Function) {
                onError(err)
            }
        }
    }
}

function deleteMedia(mediaID) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/${mediaID}`,
        method: 'DELETE',
        types: [
            Types.DELETE_MEDIA_REQUEST,
            {
                type: Types.DELETE_MEDIA_SUCCESS,
                meta: { mediaID },
            },
            Types.API_CALL_FAILURE,
        ],
    })
}

/**
 * Tries to upload a file again
 * @param {Number} mediaID
 * @deprecated
 */
function retryUploadMedia(mediaID) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/${mediaID}/retry_upload`,
        method: 'POST',
        types: [
            {
                type: Types.RETRY_UPLOAD_MEDIA_REQUEST,
                meta: { mediaID },
            },
            Types.RETRY_UPLOAD_MEDIA_SUCCESS,
            {
                type: Types.API_CALL_FAILURE,
                meta: { mediaID },
            },
        ],
    })
}

function changeMediaArray(media) {
    return {
        type: Types.CHANGE_MEDIA,
        payload: media,
    }
}

/**
 * Approves media
 * @param {Number} mediaID
 * @param {Boolean} isApproved true means is approved, false it is rejected
 * @param {Object} [opts]
 * @param {string} [opts.description]
 * @param {Boolean} [opts.inSelectedPlaylist]
 */
function approveMedia(mediaID, isApproved, opts = {}) {
    const { description, inSelectedPlaylist = false } = opts

    const body = JSON.stringify({
        approval_description: description,
        is_approved: isApproved ? Media.approvalStatus.approved : Media.approvalStatus.rejected,
    })

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/${mediaID}/approve`,
        method: 'POST',
        body,
        types: [
            {
                type: Types.APPROVE_MEDIA_REQUEST,
                meta: { mediaID, inSelectedPlaylist },
            },
            {
                type: Types.APPROVE_MEDIA_SUCCESS,
                meta: { mediaID, inSelectedPlaylist },
            },
            {
                type: Types.APPROVE_MEDIA_FAILURE,
                meta: { mediaID, inSelectedPlaylist },
            },
        ],
    })
}

/**
 * Sets media as tested
 * @param {Number} mediaID
 * @param {Boolean} isOk true means is working fine, false it doesn't work
 * @param {Object} [opts]
 * @param {string} [opts.description]
 * @param {Boolean} [opts.inSelectedPlaylist]
 */
function testMedia(mediaID, isOk, opts = {}) {
    const { description, inSelectedPlaylist = false } = opts

    const body = JSON.stringify({
        testing_description: description,
        is_tested: isOk ? Media.testingStatus.approved : Media.testingStatus.rejected,
    })

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/${mediaID}/test`,
        method: 'POST',
        body,
        types: [
            {
                type: Types.TEST_MEDIA_REQUEST,
                meta: { mediaID, inSelectedPlaylist },
            },
            {
                type: Types.TEST_MEDIA_SUCCESS,
                meta: { mediaID, inSelectedPlaylist },
            },
            {
                type: Types.TEST_MEDIA_FAILURE,
                meta: { mediaID, inSelectedPlaylist },
            },
        ],
    })
}

/**
 * Sets media push status
 * @param {Number} mediaID
 * @param {Boolean} isCompleted true means the push was successful, false it failed
 * @param {Boolean} [inSelectedPlaylist]
 */
function setMediaPushStatus(mediaID, isCompleted, inSelectedPlaylist = false) {
    return (dispatch, getState) => {
        const email = getState()?.user?.session?.email
        const body = JSON.stringify({
            media_id: mediaID,
            checked_by: email,
            status: isCompleted ? Media.pushStatus.completed : Media.pushStatus.failed,
        })

        return dispatch(
            createAction({
                endpoint: `${HUBBLE_API_TOKEN}/media/${mediaID}/push_status`,
                method: 'POST',
                body,
                types: [
                    {
                        type: Types.MEDIA_PUSH_STATUS_REQUEST,
                        meta: { mediaID, inSelectedPlaylist },
                    },
                    {
                        type: Types.MEDIA_PUSH_STATUS_SUCCESS,
                        meta: { mediaID, inSelectedPlaylist },
                    },
                    {
                        type: Types.MEDIA_PUSH_STATUS_FAILURE,
                        meta: { mediaID, inSelectedPlaylist },
                    },
                ],
            })
        )
    }
}

/**
 * Request media approval
 * @param {Number} mediaID
 * @param {Boolean} [inSelectedPlaylist]
 */
function requestMediaApproval(mediaID, inSelectedPlaylist = false) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/${mediaID}/request_approval`,
        method: 'POST',
        types: [
            {
                type: Types.APPROVAL_REQUEST_MEDIA_REQUEST,
                meta: { mediaID, inSelectedPlaylist },
            },
            {
                type: Types.APPROVAL_REQUEST_MEDIA_SUCCESS,
                meta: { mediaID, inSelectedPlaylist },
            },
            {
                type: Types.APPROVAL_REQUEST_MEDIA_FAILURE,
                meta: { mediaID, inSelectedPlaylist },
            },
        ],
    })
}

/**
 * Attaches media to a playlist
 * @param {Number} mediaID
 * @param {Number} playlistID
 * @param {Boolean} [inSelectedPlaylist] if media is being edited inside a playlist
 */
function attachMediaToPlaylist(mediaID, playlistID, inSelectedPlaylist = false) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/${mediaID}/playlist/${playlistID}`,
        method: 'PUT',
        types: [
            Types.ATTACH_MEDIA_PLAYLIST_REQUEST,
            {
                type: Types.ATTACH_MEDIA_PLAYLIST_SUCCESS,
                meta: { mediaID, playlistID, inSelectedPlaylist },
            },
            Types.API_CALL_FAILURE,
        ],
    })
}

/**
 * Detaches media to a playlist
 * @param {Number} mediaID
 * @param {Number} playlistID
 * @param {Boolean} [inSelectedPlaylist] if media is being edited inside a playlist
 */
function detachMediaToPlaylist(mediaID, playlistID, inSelectedPlaylist = false, order = 0) {
    const body = JSON.stringify({ order })
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/${mediaID}/playlist/${playlistID}`,
        method: 'DELETE',
        body,
        types: [
            Types.DETACH_MEDIA_PLAYLIST_REQUEST,
            {
                type: Types.DETACH_MEDIA_PLAYLIST_SUCCESS,
                meta: { mediaID, playlistID, inSelectedPlaylist },
            },
            Types.API_CALL_FAILURE,
        ],
    })
}

/**
 * Create new media item
 * @param {Object} media
 * @param {Number} media.date_end
 * @param {Number} media.date_start
 * @param {Number} media.length
 * @param {String} media.name
 * @param {String} media.md5
 * @param {Number} media.size
 * @param {Object} media.tags
 */
function updateMedia(media = {}) {
    const { date_end, date_start, id, name, tags } = media
    const body = JSON.stringify({
        id,
        date_end: DateUtils.formatDB(date_end),
        date_start: DateUtils.formatDB(date_start),
        name,
        tags,
    })

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/${id}`,
        method: 'PUT',
        types: [Types.UPDATE_MEDIA_REQUEST, Types.UPDATE_MEDIA_SUCCESS, Types.API_CALL_FAILURE],
        body,
    })
}

function fetchPendingApprovalCount() {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/count/approval/pending`,
        method: 'GET',
        types: [
            Types.FETCH_MEDIA_PENDING_APPROVAL_COUNT_REQUEST,
            Types.FETCH_MEDIA_PENDING_APPROVAL_COUNT_SUCCESS,
            Types.FETCH_MEDIA_PENDING_APPROVAL_COUNT_FAILURE,
        ],
    })
}

function fetchPendingPushCount() {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/count/push/pending`,
        method: 'GET',
        types: [Types.FETCH_MEDIA_PENDING_PUSH_COUNT_REQUEST, Types.FETCH_MEDIA_PENDING_PUSH_COUNT_SUCCESS, Types.FETCH_MEDIA_PENDING_PUSH_COUNT_FAILURE],
    })
}

function fetchPendingTestCount() {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/media/count/test/pending`,
        method: 'GET',
        types: [Types.FETCH_MEDIA_PENDING_TEST_COUNT_REQUEST, Types.FETCH_MEDIA_PENDING_TEST_COUNT_SUCCESS, Types.FETCH_MEDIA_PENDING_TEST_COUNT_FAILURE],
    })
}

function fetchAllMediaCounts() {
    return (dispatch) => {
        dispatch(fetchPendingApprovalCount())
        dispatch(fetchPendingPushCount())
        dispatch(fetchPendingTestCount())
    }
}

export {
    approveMedia,
    attachMediaToPlaylist,
    changeMediaArray,
    createMedia,
    deleteMedia,
    detachMediaToPlaylist,
    downloadMedia,
    downloadThumbnail,
    fetchAllMediaCounts,
    fetchMedia,
    fetchMediaDetails,
    fetchSingleMedia,
    requestMediaApproval,
    retryUploadMedia,
    setMediaPushStatus,
    testMedia,
    updateMedia,
    uploadCompleted,
    uploadMedia,
}
